import React from 'react';
import { useIntl } from 'react-intl';

import type { IApiResponseFoodProductDetails } from '@/shared/api/menu/types';
import { FreeGiftTag } from '@/shared/components/FreeGiftTag';
import { Modifiers } from '@/shared/components/Modifires';
import { ModalProduct } from '@/shared/components/ProductDetails';
import type { TDetailsPropForHandler } from '@/shared/components/ProductDetails/types';
import { Fulfillment, FulfillmentType } from '@/shared/constants';
import { useAppDispatch } from '@/shared/hooks/appRedux';
import { useCurrencyFormmater } from '@/shared/hooks/currencyFormmater';
import { MODAL_ARIA_LABELLEDBY, ModalService } from '@/shared/services/modal';
import type { ICartLineItems } from '@/shared/types/cart';

import { FoodProductDetails, RemoveItemByGroupId, UpdateFoodProductToCart } from '../../../index';
import {
  ButtonOrder,
  DIVContainer,
  DivRowModifires,
  DivRowPrice,
  DivRowTable,
  DivRowTableQTY,
  HeaderDivRowTable,
  RowEachPrice,
  RowPrice,
  TBodyOrder,
  TD,
  TH,
  THQTY,
  THeadOrder,
  TR,
  TableOrder,
  WrapperPrice,
} from '../parts';

interface IProps {
  listItems: Nullable<ICartLineItems[]>;
  basketName: string;
  productEditUrl: Nullable<string>;
  orderType: string;
  orderTypes: {
    Service: string;
    Merchandise: string;
    Food: string;
  };
}
const OrderTable: React.FC<IProps> = ({
  listItems,
  basketName,
  orderType,
  orderTypes,
  productEditUrl,
}) => {
  const setFormatter = useCurrencyFormmater();
  const dispatch = useAppDispatch<CartAppDispatch>();
  const { formatMessage } = useIntl();
  const messages = {
    edit: formatMessage({ id: 'Edit' }),
    remove: formatMessage({ id: 'Remove' }),
    qty: formatMessage({ id: 'Qty' }),
    item: formatMessage({ id: 'Item' }),
    price: formatMessage({ id: 'Price' }),
    each: formatMessage({ id: 'Checkout.Cart.Labels.Each' }),
    editAriaLabel: formatMessage({ id: 'Checkout.Cart.Buttons.Edit.AriaLabel' }),
    removeAriaLabel: formatMessage({ id: 'Checkout.Cart.Buttons.Remove.AriaLabel' }),
    buttonProductModal: formatMessage({ id: 'Checkout.Cart.Modals.Product.Buttons.Update' }),
  };

  const getFulFilment = (
    IsPickup: boolean,
    FulfillmentDate: string,
    FulfillmentTime: string,
    FulfillmentInstructions: string,
    DeliveryLocation: string,
    IsFulfillmentFilled: boolean,
    PickUpArea: Nullable<string>,
    FulfillmentDateTime: string,
  ): FulfillmentType => {
    if (!IsFulfillmentFilled) return Fulfillment;

    const fulFilment = structuredClone(Fulfillment);
    fulFilment.PickUpLocation = PickUpArea || '';
    fulFilment.SelectedDate = FulfillmentDateTime;
    if (IsPickup) {
      fulFilment.PickUpFulfillment.PickUpTime = FulfillmentTime;
      fulFilment.PickUpFulfillment.PickUpDate = FulfillmentDate;
      fulFilment.PickUpFulfillment.RecipientInstructions = FulfillmentInstructions;
      return fulFilment;
    }
    fulFilment.DeliveryFulfillment.DeliveryDate = FulfillmentDate;
    fulFilment.DeliveryFulfillment.DeliveryTime = FulfillmentTime;
    fulFilment.DeliveryFulfillment.DeliveryLocation = DeliveryLocation;
    fulFilment.DeliveryFulfillment.RecipientInstructions = FulfillmentInstructions;
    fulFilment.SelectedFulfillmentMethod = 'Delivery';
    return fulFilment;
  };

  const handleEdit = (
    ProductId: string,
    Id: string,
    root: string,
    IsPickup: boolean,
    FulfillmentDate: string,
    FulfillmentTime: string,
    FulfillmentInstructions: string,
    DeliveryLocation: string,
    IsFulfillmentFilled: boolean,
    PickUpArea: Nullable<string>,
    FulfillmentDateTime: string,
  ): void => {
    if (orderType === orderTypes.Food) {
      const fulfillment = getFulFilment(
        IsPickup,
        FulfillmentDate,
        FulfillmentTime,
        FulfillmentInstructions,
        DeliveryLocation,
        IsFulfillmentFilled,
        PickUpArea,
        FulfillmentDateTime,
      );
      dispatch(
        FoodProductDetails(
          {
            basket: basketName,
            prod: ProductId,
            item: Id,
          },
          (productDetailsWithModifiers: IApiResponseFoodProductDetails): void => {
            const handlerUpdate = (
              updateData: TDetailsPropForHandler,
              closeModal: VoidFunction,
              setShowWarningMsg: (msg: string | null) => void,
            ): void => {
              dispatch(
                UpdateFoodProductToCart(
                  {
                    item: Id,
                    prod: ProductId,
                    basket: basketName,
                    root,
                    Modifiers: updateData.Product.Modifiers,
                    SelectedProductQuantity: updateData.UserOrderingInfo?.SelectedProductQuantity,
                    RecipientInstructions: updateData.RecipientInstructions,
                  },
                  (errorMsgForModal?: string) => {
                    if (errorMsgForModal) {
                      setShowWarningMsg(errorMsgForModal);
                      return;
                    }
                    closeModal();
                  },
                ),
              );
            };
            ModalService.open(ModalProduct, {
              product: productDetailsWithModifiers.Product,
              productDetailsWithModifiers,
              isOrderAllowed: true,
              fulfillment,
              handlerFooter: handlerUpdate,
              nameButton: messages.buttonProductModal,
              ariaLabelledby: `${MODAL_ARIA_LABELLEDBY} modal_product_tab`,
            });
          },
        ),
      );
    } else if (
      productEditUrl !== null &&
      productEditUrl !== '' &&
      basketName !== null &&
      basketName !== ''
    ) {
      const editUrl =
        `${window.location.origin}${productEditUrl}` +
        `?basket=${basketName}&item=${Id}&prod=${ProductId}&root=${root}&cart=1`;
      window.location.assign(editUrl);
    }
  };

  const handleRemove = (item: ICartLineItems): void => {
    dispatch(RemoveItemByGroupId(item?.GroupId, basketName, () => {}));
    if (window?.gaGtag) {
      const productData = {
        item_id: item.ProductId,
        item_name: item.ProductName,
        item_category: item.CatalogId,
        price: item.OriginalPrice,
        quantity: item.Quantity,
      };
      window.gaGtag.removeProductFromCart(productData);
    }
  };

  const getBodyOneOrder = listItems?.map((item) => {
    const isEach: boolean = item.Quantity > 1;
    const id: string = item.Id;

    return (
      <TR key={id} style={{ backgroundColor: 'transparent' }}>
        <THQTY scope="row">
          <DivRowTableQTY width="25px">{item.Quantity}</DivRowTableQTY>
        </THQTY>
        <TD>
          <DivRowTable width="170px">{item.ProductName}</DivRowTable>
          {!!item.ModifierItems && !!item.ModifierItems.length && (
            <DivRowModifires width="100px">
              <Modifiers modifiers={item.ModifierItems} />
            </DivRowModifires>
          )}
          {item.IsFreeGift ? (
            <DivRowModifires>
              <FreeGiftTag />
            </DivRowModifires>
          ) : (
            <DIVContainer width="fit-content">
              <ButtonOrder
                className="ButtonEditOrder"
                aria-label={`${messages.editAriaLabel} ${item.ProductName}`}
                aria-disabled="false"
                role="button"
                onClick={() =>
                  handleEdit(
                    item.ProductId,
                    item.Id,
                    basketName,
                    item.IsPickup,
                    item.FulfillmentDate,
                    item.FulfillmentTime,
                    item.FulfillmentInstructions,
                    item.DeliveryLocation,
                    item.IsFulfillmentFilled,
                    item.PickUpArea,
                    item.FulfillmentDateTime,
                  )
                }
                dangerouslySetInnerHTML={{
                  __html: messages.edit,
                }}
              />
              <ButtonOrder
                className="ButtonRemoveOrder"
                aria-label={`${messages.removeAriaLabel} ${item.ProductName}`}
                aria-disabled="false"
                role="button"
                onClick={() => handleRemove(item)}
                dangerouslySetInnerHTML={{
                  __html: messages.remove,
                }}
              />
            </DIVContainer>
          )}
        </TD>
        <TD>
          <WrapperPrice>
            <RowPrice width="80px">{setFormatter(item.OriginalPrice * item.Quantity)}</RowPrice>
            {isEach && (
              <RowEachPrice
                width="80px"
                dangerouslySetInnerHTML={{
                  __html: `${messages.each} ${setFormatter(item.OriginalPrice)}`,
                }}
              />
            )}
          </WrapperPrice>
        </TD>
      </TR>
    );
  });

  return (
    <TableOrder className="TableOrder">
      <THeadOrder className="THeadOrder">
        <TR>
          <TH scope="col">
            <HeaderDivRowTable
              width="27px"
              float="left"
              dangerouslySetInnerHTML={{
                __html: messages.qty,
              }}
            />
          </TH>
          <TH scope="col">
            <HeaderDivRowTable
              width="180px"
              float="center"
              dangerouslySetInnerHTML={{
                __html: messages.item,
              }}
            />
          </TH>
          <TH scope="col">
            <DivRowPrice
              width="80px"
              float="right"
              dangerouslySetInnerHTML={{
                __html: messages.price,
              }}
            />
          </TH>
        </TR>
      </THeadOrder>
      <TBodyOrder className="TBodyOrder">{getBodyOneOrder}</TBodyOrder>
    </TableOrder>
  );
};

export default OrderTable;
